import {Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {SidebarService} from "../../services/sidebar.service";
import {dialogOptionalVars, UtilService} from "../../services/util.service";
import {StateService} from "../../services/state.service";
import {PartiesService} from "../../services/parties.service";
import {ActivatedRoute} from "@angular/router";
import {PartiesResponse} from "../../models/parties";
import {IntervenorCompanyInfo, User, UserRole} from "../../models/user";
import {EditControlsComponent} from "../edit-controls/edit-controls.component";
import {NgForm} from "@angular/forms";
import {EPDSFile} from "../../models/file-upload";
import {catchError, Subscription, tap, throwError} from "rxjs";
import {FileUploadService} from "../../services/file-upload.service";
import {AttorneyInfoResponse} from "../../models/docket";
import {CaseDocketService} from "../../services/case-docket.service";
import {ProtestInfo} from "../../models/protest";
import PlaceResult = google.maps.places.PlaceResult;

@Component({
    selector: 'app-parties',
    templateUrl: './parties.component.html',
    styleUrls: ['./parties.component.scss'],
    standalone: false
})
export class PartiesComponent implements OnInit, OnDestroy {

  protected readonly UserRole = UserRole;

  @ViewChild('partyForm', {read: NgForm}) partyForm!: NgForm;
  @ViewChild('intervenorForm', {read: NgForm}) intervenorForm!: NgForm;
  @ViewChild('orphanForm', {read: NgForm}) orphanForm!: NgForm;

  a_No = '';
  partiesResponse!: PartiesResponse;
  emailListOutlook!: string;
  partyFormData?: any;
  intervenorFormData!: any[];
  orphanFormData!: any[];
  protestInfo!: ProtestInfo;
  docketSub!: Subscription;

  isGAOUserWithFullAccess = false;
  isAgencyPOCUser = false;
  isFirmIdSame = false;
  canPrimaryAgencyAddAgencyBtnBeDisplayed = false;
  canSecAgencyAddAgencyBtnBeDisplayed = false;
  agencyUser = false;

  isIntervenorFormVisible = false;

  savingDetails = { index: -1, partyIndex: -1, saving: false };

  constructor(public util: UtilService,
              public stateService: StateService,
              private readonly activatedRoute: ActivatedRoute,
              private readonly sidebarService: SidebarService,
              private readonly partiesService: PartiesService,
              private readonly fileUploadService: FileUploadService,
              private caseDocketService: CaseDocketService) {
  }

  ngOnInit() {
    this.a_No = this.activatedRoute.snapshot.params['a_No'];
    this.caseDocketService.loadDocket(this.a_No);
    this.docketSub = this.caseDocketService.docketSub$.subscribe((response: AttorneyInfoResponse|undefined) => {
      if (!response) return;
      this.protestInfo = response.protestInfo;
      this.getListOfParties();
      this.sidebarService.setNavByUser(this.stateService.user.role_id, response.protestInfo);
    });
  }

  ngOnDestroy(): void {
    if (this.a_No) {
      this.docketSub.unsubscribe();
    }
  }

  getListOfParties() {
    this.savingDetails.saving = true;

    this.partiesService.getListOfParties(this.a_No).subscribe(partiesResponse => {
      this.partiesResponse = partiesResponse;
      console.debug(this.partiesResponse);

      this.setVars();
      this.savingDetails.saving = false;
    });
  }

  setVars() {
    if (this.partiesResponse) {
      this.emailListOutlook = this.partiesResponse.emailList.replace(/,/g, ';');

      this.agencyUser = ((this.stateService.userRole === UserRole.Agency_Admin) || (this.stateService.userRole === UserRole.Agency_Attorney));
      this.isGAOUserWithFullAccess = this.util.isGAOUserWithFullAccess(this.protestInfo.viewOnly);
      this.isAgencyPOCUser = this.stateService.userRole === UserRole.Agency_Admin;
      this.isFirmIdSame = (+this.stateService.user.firm_id === this.protestInfo.agency_Info_Id) || this.partiesResponse.primaryAgencyInfoIds.includes(this.stateService.user.firm_id);

      this.canPrimaryAgencyAddAgencyBtnBeDisplayed = false;
      if (this.partiesResponse.primary_agency_list?.length <= 0) {
        if (this.isGAOUserWithFullAccess || (this.isAgencyPOCUser && this.isFirmIdSame)) {
          this.canPrimaryAgencyAddAgencyBtnBeDisplayed = true;
        }
      }

      this.canSecAgencyAddAgencyBtnBeDisplayed = false;

      if (this.isGAOUserWithFullAccess && (this.partiesResponse.secondary_agency_list?.length <= 0)) {
        this.canSecAgencyAddAgencyBtnBeDisplayed = true;
      }

      if (this.partiesResponse.listOfIntervenorParty.length > 0)
        this.intervenorFormData = new Array<any>(this.partiesResponse.listOfIntervenorParty.length).fill({});

      if (this.partiesResponse.orphanIntComDetailList.length > 0)
        this.orphanFormData = new Array<any>(this.partiesResponse.orphanIntComDetailList.length).fill({});
    }
  }

  onPartyEdit() {
    this.partyFormData = JSON.parse(JSON.stringify(this.protestInfo));
  }

  onIntervenorEdit(info: User, formData: any) {
    const address = info.intervenor_Company_Address;
    const addressParts = address && address.split(/\r|\n|,/);
    const addressLength = addressParts?.length;
    const address1 = addressParts && addressParts[0] || "";
    const address2 = (addressLength == 6) ? addressParts && addressParts[addressParts.length -5] || null : null;
    const city = addressParts && addressParts[addressLength -4] || null;
    const state = addressParts && addressParts[addressLength -3].trim() || null;
    const zipCode = addressParts && addressParts[addressLength -2].trim() || null;
    const country = addressParts && addressParts[addressLength -1] || null;

    // formData = {};

    formData.a_No = this.a_No;
    formData.intervenorFileId = info.intervenorCompanyInfo?.intervenorFileId;
    formData.companyAddress = info.intervenorCompanyInfo?.companyAddress;
    formData.companyDetail = info.intervenorCompanyInfo?.companyDetail;

    formData.company_Name = info.intervenor_Company_Name;
    formData.oldCompanyName = info.intervenor_Company_Name;
    formData.companyAddress = address;
    formData.company_address1 = address1;
    formData.company_address2 = address2!;
    formData.company_City = city!;
    formData.company_State = state!;
    formData.company_Zipcode = zipCode!;
    formData.company_Country = country!;
  }

  onPartyUpdate(control: EditControlsComponent, form: NgForm, partyType: string, formData: any) {
    console.debug('onPartyUpdate()');
    console.debug(form.valid);
    console.debug(formData);


    if (formData) {
      this.savingDetails.saving = true;

      this.partiesService.updatePartyInfo(formData, partyType).subscribe(() => {
        this.getListOfParties();
        this.util.displayDialogMessage('Success', partyType + ' has been updated.').subscribe((resp) => {
          control.editMode = false;
        });
      });
    }
  }


  addPartiesToTheCase(typeOfRequest: string, invitorType: string, savingDetails: { saving: boolean, index: number }): void {
    const agencyDialogParams: dialogOptionalVars = invitorType === 'A' ? {
      cancelBtn: true,
      getSelectResponse: true,
      selectReq: true,
      selectData: this.partiesResponse.agencyAttorneyEmailIds
    } : {
        cancelBtn: true,
        getResponse: true,
        responseReq: true
    };

    let agencyDialogTitle= '';
    let agencyDialogMessage = '';
    let confirmDialogTitle = 'Representative Info';
    let emailLov = false;

    switch (typeOfRequest) {
      case 'Assign Agency Representative': {
        confirmDialogTitle = 'Attorney Info'

        if (invitorType === 'A') {
          agencyDialogTitle = typeOfRequest;
          agencyDialogMessage = 'Select Attorney';
          emailLov = true;
        } else {
          agencyDialogTitle = 'Assign Secondary Agency Representative';
          agencyDialogMessage = 'Enter Secondary Agency Attorney Email Address';
        }

        break;
      }
      case 'Primary Representative': {
        agencyDialogTitle = 'Assign Primary Representative';
        agencyDialogMessage = 'Enter Representative Email Address';
        confirmDialogTitle = 'Primary Representative Info';
        break;
      }
      case 'Secondary Representative': {
        agencyDialogTitle = 'Add Secondary Representative';
        agencyDialogMessage = 'Enter Representative Email Address';
        confirmDialogTitle = 'Secondary Representative Info';

        break;
      }
    }

    this.util.displayDialogMessage(agencyDialogTitle, agencyDialogMessage, agencyDialogParams).subscribe((agencyDialogResp) => {
      console.debug(agencyDialogResp);
      const email = emailLov ? agencyDialogResp?.selectResponse : agencyDialogResp?.response;

      if (agencyDialogResp && email && this.partiesResponse) {
        this.savingDetails.saving = true;

        this.partiesService.getPrimaryRepsOrAgencyRepsInfo(email, invitorType, typeOfRequest, '', '', this.a_No).subscribe((attorneyInfoResp) => {
          console.debug(attorneyInfoResp);

          if (attorneyInfoResp) {
            const user = attorneyInfoResp.attorney_Info;
            const errorMsg = this.partiesService.validateInvitationRequests(attorneyInfoResp, typeOfRequest, invitorType, '', '', this.a_No);

            if (errorMsg) {
              this.util.displayDialogMessage('Error', errorMsg);

              savingDetails.saving = false;
            } else {
              const confirmMessage = 'You are adding <strong>' + user.first_Name + ' ' + user.last_Name + '</strong> from <strong>' + user.firm_Name + '</strong>';

              const confirmDialogParams: dialogOptionalVars = {
                cancelBtn: true,
                confirmText: 'Confirm'
              }

              this.util.displayDialogMessage(confirmDialogTitle, confirmMessage, confirmDialogParams).subscribe((confirmResp) => {
                console.debug(confirmResp);

                if (confirmResp) {
                  this.partiesService.confirmPrimaryOrAgencyRepInfo(email, invitorType, typeOfRequest, '', '', this.a_No)
                  .subscribe((addResp) => {
                    console.debug(addResp);

                    if (addResp) {
                      const successMsg = `<strong>${user.first_Name} ${user.last_Name}</strong> has been successfuly added.`

                      this.getListOfParties();
                      this.util.displayDialogMessage('Success', successMsg).subscribe((resp) => {
                      });
                    } else {
                      savingDetails.saving = false;
                    }
                  })
                } else {
                  savingDetails.saving = false;
                }
              })
            }
          } else {
            console.error(attorneyInfoResp);
            this.util.displayDialogMessage('Error', 'There was a problem retrieving Attorney Information');

            savingDetails.saving = false;
          }
        });
      } else {
        savingDetails.saving = false;
      }
    });
  }

  onIntervenorSubmit() {
    this.getListOfParties();
    this.isIntervenorFormVisible = false;
  }

  onIntervenorCancel() {
    this.isIntervenorFormVisible = false;
  }

  onIntervenorAdd() {
    this.isIntervenorFormVisible = true;
  }

  removeIntervenorByUser(intervenorInfo: User) {
    this.removeIntervenor(intervenorInfo.intervenorCompanyInfo);
  }
  removeIntervenor(intervenorInfo: IntervenorCompanyInfo) {
    const companyInfo = intervenorInfo;
    const companyName = companyInfo?.companyName || '';

    const confirmMessage = 'Are you sure you want to remove this intervenor\'s (' + companyName + ') access from this case?';
    const successMsg = 'You have successfully removed this intervenor\'s (' + companyName + ') access from this case.';
    const errorMsg = 'Unable to remove this intervenor\'s (' + companyName + ') access from this case.';

    const confirmDialogParams: dialogOptionalVars = {
      cancelBtn: true
    }

    this.util.displayDialogMessage('Warning', confirmMessage, confirmDialogParams).subscribe((confirmResp) => {
      console.debug(confirmResp);

      if (confirmResp && companyInfo) {
        this.savingDetails.saving = true;

        this.partiesService.removeIntervenor(this.a_No, companyInfo).subscribe(resp => {
          console.debug(resp);

          if (resp) {
            this.getListOfParties();
            this.util.displayDialogMessage('Success', successMsg).subscribe();
          } else {
            this.util.displayDialogMessage('Error', errorMsg).subscribe();
            this.savingDetails.saving = false;
          }
        });
      }
    });
  }

  protected readonly JSON = JSON;

  placeChanged(place: PlaceResult, formData: any) {
    let streetAddr = place.formatted_address?.split(",")[0]
    if (place.name !== streetAddr) {
      streetAddr = place.name + "," + streetAddr;
    }
    formData.company_address1 = streetAddr;

    place.address_components?.forEach(component => {
      const types = component.types;
      if (types.includes('locality')) {
        formData.company_City = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        formData.company_State = component.long_name;
      } else if (types.includes('country')) {
        formData.company_Country = component.long_name;
      } else if (types.includes('postal_code')) {
        formData.company_Zipcode = component.long_name;
      }
    });
  }

}
